import api from '../api/config';

/**
 * Extracts a consistent error message from an API error.
 * This can be shared across different services.
 * @param {Error} error The error object from a catch block.
 * @returns {string} A user-friendly error message.
 */
const extractErrorMessage = (error) => {
  if (error.response?.data) {
    const data = error.response.data;

    // Handle different error formats from the backend
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;

    // Handle Django Rest Framework field-specific errors
    if (data.email && Array.isArray(data.email)) return `Email: ${data.email[0]}`;
    if (data.password && Array.isArray(data.password)) return `Password: ${data.password[0]}`;
    if (data.non_field_errors && Array.isArray(data.non_field_errors)) return data.non_field_errors[0];

    // Generic handler for object with multiple field errors
    const firstError = Object.values(data).find(value => Array.isArray(value) && value.length > 0);
    if (firstError) return firstError[0];
  }

  // Fallback error messages
  if (error.response?.status === 401) return "Authentication failed. Please log in again.";
  if (error.response?.status === 400) return "Invalid request. Please check your input.";
  if (error.response?.status >= 500) return "A server error occurred. Please try again later.";

  return "An unexpected error occurred. Please try again.";
};

const searchInstitutes = async (query) => {
  try {
    const response = await api.get(`/api/institutes/search/?search=${query}`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getCourses = async () => {
  try {
    const response = await api.get('/api/courses/');
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getCourseById = async (courseId) => {
  try {
    const response = await api.get(`/api/courses/${courseId}/`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const createCourse = async (courseData) => {
  try {
    const response = await api.post('/api/courses/', courseData);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const updateCourse = async (courseId, courseData) => {
  try {
    const response = await api.put(`/api/courses/${courseId}/`, courseData);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const deleteCourse = async (courseId) => {
  try {
    await api.delete(`/api/courses/${courseId}/`);
    return { success: true };
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const createCourseFromFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('course_file', file);

    const response = await api.post('/api/courses/create-from-file/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getAQMSCourses = async () => {
  try {
    const response = await api.get('/api/courses/aqms/list/');
    // Handle both response formats: {success: true, data: [...]} or direct array
    if (response.data.success !== undefined) {
      return response.data.data || [];
    }
    return response.data || [];
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const importCourseFromAQMS = async (aqmsCourseId, email = null) => {
  try {
    const payload = { aqms_course_id: aqmsCourseId };
    if (email) {
      payload.email = email;
    }
    const response = await api.post('/api/courses/aqms/import/', payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const createLesson = async (lessonData) => {
  try {
    const response = await api.post('/api/lessons/', lessonData);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const uploadDocument = async (file, fileType, lessonId, serialNumber, category, onUploadProgress) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', fileType);
    formData.append('lesson_id', lessonId);
    formData.append('serialNumber', serialNumber);
    formData.append('category', category);

    const response = await api.post('/api/upload_lesson_doc/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        if (progressEvent.lengthComputable) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Ensure onUploadProgress is a function before calling it
          if (typeof onUploadProgress === 'function') {
            onUploadProgress(percentCompleted);
          }
        }
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Step 1: Generate SAS URL for direct Azure upload
 * @param {string} filename Original filename with extension
 * @param {string} fileType Type of file (lesson_doc, syllabus, lesson_plan)
 * @param {number} lessonId ID of the lesson (required for lesson_doc)
 * @returns {Promise<any>}
 */
const generateUploadUrl = async (filename, fileType, lessonId) => {
  try {
    const payload = {
      filename,
      file_type: fileType
    };
    
    if (lessonId) {
      payload.lesson_id = lessonId;
    }

    const response = await api.post('/api/generate-upload-url/', payload);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Step 3: Confirm file upload after Azure upload completes
 * @param {string} blobName Blob name from generate URL response
 * @param {string} filename Original filename
 * @param {string} filetype Type of file (pdf, txt, video)
 * @param {number} lessonId ID of the lesson
 * @param {string} category Document category
 * @param {number} serialNumber Serial number for ordering
 * @param {string} containerName Container name
 * @returns {Promise<any>}
 */
const confirmFileUpload = async (blobName, filename, filetype, lessonId, category, serialNumber, containerName) => {
  try {
    const payload = {
      blob_name: blobName,
      filename,
      filetype,
      category,
      serialNumber: serialNumber || 0,
    };

    if (lessonId) {
      payload.lesson_id = lessonId;
    }

    if (containerName) {
      payload.container_name = containerName;
    }

    const response = await api.post('/api/confirm-file-upload/', payload);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Step 2: Upload file directly to Azure Blob Storage
 * @param {string} uploadUrl The SAS URL from generateUploadUrl
 * @param {File} file The file to upload
 * @returns {Promise<any>}
 */
const uploadToAzureBlob = async (uploadUrl, file) => {
  try {
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'application/octet-stream'
      },
      body: file
    });

    if (!uploadResponse.ok) {
      return {
        success: false,
        error: `Azure upload failed with status ${uploadResponse.status}`
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to upload to Azure'
    };
  }
};

const getLessonById = async (lessonId) => {
  try {
    const response = await api.get(`/api/lessons/${lessonId}/`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const generateQuestions = async (lessonId, payload) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/generate_questions/`, payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const generateAdvancedQuestions = async (lessonId, payload) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/questions/`, payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const saveQuestionSet = async (payload) => {
  try {
    const response = await api.post('/api/question-sets/save/', payload);
    return response.data;
  } catch (error) {
    // In case of save, a specific alert might be better than a generic error.
    // For now, we'll throw a detailed error message.
    const errorMessage = extractErrorMessage(error);
    alert(`Failed to save quiz: ${errorMessage}`); // Provide immediate feedback
    throw new Error(errorMessage);
  }
};

const summarizeLesson = async (lessonId, docsourceId) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/summarize/`, {
      document_id: docsourceId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Edits the transcription for a specific document within a lesson.
 * @param {number} lessonId The ID of the lesson.
 * @param {number} documentId The ID of the document resource.
 * @param {string} editedText The edited transcription text.
 * @returns {Promise<any>}
 */
const editTranscription = async (lessonId, documentId, editedText) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/edit-transcription/`, {
      document_id: documentId,
      edited_text: editedText
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Edits the summarization for a specific document within a lesson.
 * @param {number} lessonId The ID of the lesson.
 * @param {number} documentId The ID of the document resource.
 * @param {string} editedText The edited summary text.
 * @returns {Promise<any>}
 */
const editSummarization = async (lessonId, documentId, editedText) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/edit-summarization/`, {
      document_id: documentId,
      edited_text: editedText
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Checks the summarization status for a specific document within a lesson.
 * @param {number} lessonId The ID of the lesson.
 * @param {number} documentId The ID of the document resource being summarized.
 * @returns {Promise<any>}
 */
const checkSummarizeStatus = async (lessonId, documentId) => {
  try {
    const response = await api.get(`/api/lessons/${lessonId}/summarize-status/?document_id=${documentId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Starts a transcription job for a specific document within a lesson.
 * @param {number} lessonId The ID of the lesson.
 * @param {number} documentId The ID of the document resource to transcribe.
 * @returns {Promise<any>}
 */
const startTranscriptionJob = async (lessonId, documentId) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/transcribe/`, {
      document_id: documentId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Checks the transcription status of a lesson using external API.
 * @param {number} lessonId The ID of the lesson to check.
 * @param {string} videoIndex
 * @param {string} videoAccessToken
 * @returns {Promise<any>}
 */
const checkTranscriptionJobStatus = async (lessonId, videoIndex, videoAccessToken, documentId) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/transcription_job/`, {
      video_index: videoIndex,
      video_access_token: videoAccessToken,
      document_id: documentId
    });
    // Assuming the response will contain the updated transcript or status
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Starts a Speech Services transcription job for a specific document within a lesson.
 * @param {number} lessonId The ID of the lesson.
 * @param {number} documentId The ID of the document resource to transcribe.
 * @returns {Promise<any>}
 */
const startSpeechTranscriptionJob = async (lessonId, documentId) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/speech-transcribe/`, {
      document_id: documentId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Checks the Speech Services transcription status for a specific document.
 * @param {number} lessonId The ID of the lesson.
 * @param {number} documentId The ID of the document resource.
 * @returns {Promise<any>}
 */
const checkSpeechTranscriptionJobStatus = async (lessonId, documentId) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/speech-transcription-job/`, {
      document_id: documentId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

const getLessonsByCourse = async (courseId) => {
  try {
    const response = await api.get(`/api/lessons/by_course/`, {
      params: { course_id: courseId }
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetches all question sets for a specific lesson.
 * @param {string} lessonId - The ID of the lesson.
 * @returns {Promise<Array<any>>} A promise that resolves to an array of question sets.
 * @throws {Error} Throws an error if the API call fails.
 */
const getQuestionSetsByLesson = async (lessonId) => {
  try {
    const response = await api.get(`/api/question-sets/by_lesson/?lesson_id=${lessonId}`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getQuestionSetById = async (questionSetId) => {
  try {
    const response = await api.get(`/api/question-sets/${questionSetId}/?mode=deep`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const submitQuestionReview = async (questionId, rating, comment = null) => {
  try {
    const payload = {
      question: questionId,
      rating: rating,
    };
    if (comment) {
      payload.comment = comment;
    }
    const response = await api.post('/api/question-reviews/', payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const exportQuestionSetToAQMS = async (questionSetId, questionIds = null) => {
  try {
    const payload = {
      question_set_id: questionSetId
    };
    
    // Add question_ids only if provided
    if (questionIds && questionIds.length > 0) {
      payload.question_ids = questionIds;
    }
    
    const response = await api.post('/api/question-sets/export-to-aqms/', payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getLLMUsage = async () => {
  try {
    const response = await api.get('/api/llm-usage/');
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getLLMUsageSummary = async () => {
  try {
    const response = await api.get('/api/llm-usage/me/');
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getQuestionById = async (questionId) => {
  try {
    const response = await api.get(`/api/questions/${questionId}/`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const updateQuestion = async (questionId, payload) => {
  try {
    const response = await api.put(`/api/questions/${questionId}/update/`, payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const enhanceQuestion = async (questionId, prompt) => {
  try {
    const response = await api.post(`/api/questions/${questionId}/enhance/`, { prompt });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const deleteQuestion = async (questionId) => {
  try {
    const response = await api.delete(`/api/questions/${questionId}/`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getDashboardStats = async () => {
  try {
    const response = await api.get('/api/dashboard/stats/');
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getUsersWithCounts = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Set defaults and apply params
    const page = params.page || 1;
    const pageSize = params.page_size || 10;
    const ordering = params.ordering || 'date_joined';

    queryParams.append('page', page.toString());
    queryParams.append('page_size', pageSize.toString());
    queryParams.append('ordering', ordering);

    const response = await api.get(`/api/users-with-counts/?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Starts asynchronous question generation for a lesson (unified for both simple and advanced)
 * @param {number} lessonId The ID of the lesson
 * @param {object} payload Generation parameters with mode ('simple' or 'advanced')
 * @returns {Promise<any>} Returns response_id and question_set_id for polling
 */
const startQuestionGeneration = async (lessonId, payload) => {
  try {
    const response = await api.post(`/api/lessons/${lessonId}/generate_questions/`, payload);
    // Expecting 202 status with response_id and question_set_id
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

/**
 * Checks the status of question generation (unified for both simple and advanced)
 * @param {number} lessonId The ID of the lesson
 * @param {number} questionSetId The question set ID
 * @returns {Promise<any>}
 */
const checkQuestionGenerationStatus = async (lessonId, questionSetId) => {
  try {
    const response = await api.get(
      `/api/lessons/${lessonId}/generate_questions_status/?question_set_id=${questionSetId}`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
};

// Audit

const runAudit = async (questionSetId) => {
  try {
    const response = await api.post('/api/audit-runs/run/', {
      question_set_id: parseInt(questionSetId)
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getQuestionSetSourceFileUrl = async (questionSetId) => {
  try {
    const response = await api.get(`/api/question-sets/${questionSetId}/download-source/`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const downloadAuditCsv = async (auditRunId) => {
  try {
    const response = await api.get(`/api/audit-runs/${auditRunId}/download_csv/`, {
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const downloadAuditPdf = async (auditRunId) => {
  try {
    const response = await api.get(`/api/audit-runs/${auditRunId}/download_pdf/`, {
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// Outcomes

const getOutcomesByCourse = async (courseId) => {
  try {
    const response = await api.get(`/api/outcomes/by_course/?course_id=${courseId}`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const createOutcome = async (payload) => {
  try {
    const response = await api.post('/api/outcomes/', payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// Blueprint

const uploadBlueprint = async (courseId, file) => {
  try {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/api/courses/${courseId}/upload-blueprint/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const downloadBlueprint = async (courseId) => {
  try {
    const response = await api.get(`/api/courses/${courseId}/download-blueprint/`, {
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// Question Sets

const uploadQuestionSetCsv = async (lessonId, file, name) => {
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('lesson_id', lessonId);
    if (name) form.append('name', name);
    const response = await api.post('/api/question-sets/upload-csv/', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const uploadQuestionPaper = async (lessonId, file, name) => {
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('lesson_id', lessonId);
    if (name) form.append('name', name);
    const response = await api.post('/api/question-sets/upload-qpaper/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const downloadQuestionSetExcel = async (questionSetId) => {
  try {
    const response = await api.get(`/api/question-sets/${questionSetId}/download-excel/`, {
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Download a canonical upload template CSV ("qpaper" or "blueprint") and
 * trigger a save in the browser. Used by the "Download Template" buttons
 * next to the Upload Question Paper / Upload Blueprint controls.
 *
 * Backend endpoints (added Apr 27 bug-bash fix):
 *   GET /api/question-sets/template/qpaper/
 *   GET /api/question-sets/template/blueprint/
 */
const downloadQuestionSetTemplate = async (kind = 'qpaper') => {
  const path = kind === 'blueprint'
    ? '/api/question-sets/template/blueprint/'
    : '/api/question-sets/template/qpaper/';
  try {
    const response = await api.get(path, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = kind === 'blueprint' ? 'blueprint_template.csv' : 'qpaper_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getQuestionSetsByLessonQuery = async (lessonId) => {
  try {
    const response = await api.get(`/api/question-sets/?lesson=${lessonId}`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getQuestionSetByQuestionSetId = async (questionSetId) => {
  try {
    const response = await api.get(`/api/question-sets/by_question_set/?question_set_id=${questionSetId}`);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const createQuestionSet = async (payload) => {
  try {
    const response = await api.post('/api/question-sets/', payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const generateQuestionSet = async (payload) => {
  try {
    const response = await api.post('/api/question-sets/generate/', payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// Questions

const createQuestion = async (payload) => {
  try {
    const response = await api.post('/api/questions/', payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const createQuestionsBulk = async (payload) => {
  try {
    const response = await api.post('/api/questions/bulk/', payload);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// Lesson with SAS URL

const getLessonByIdWithSas = async (lessonId) => {
  try {
    const response = await api.get(`/api/lessons/${lessonId}/`, {
      params: { include_sas: true }
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// IMSCC raw file helpers (used by ImportLMS) — return Blob

const getImsccRawFileBlob = async (payload) => {
  try {
    const response = await api.post('/api/imscc/raw-file', payload, { responseType: 'blob' });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const getImsccRawFileAsPdfBlob = async (payload) => {
  try {
    const response = await api.post('/api/imscc/raw-file-as-pdf', payload, { responseType: 'blob' });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// Course create with timestamp fallback on duplicate title (used by ImportLMS)

const createCourseWithTimestampFallback = async (courseData) => {
  try {
    const response = await api.post('/api/courses/', courseData);
    return response.data;
  } catch (error) {
    if (error.response?.data?.existing_course_id || error.response?.status === 400) {
      const retryData = {
        ...courseData,
        title: `${courseData.title} (${new Date().toLocaleString().replace(/[/:]/g, '-')})`
      };
      const retryResponse = await api.post('/api/courses/', retryData);
      return retryResponse.data;
    }
    throw new Error(extractErrorMessage(error));
  }
};

// LST upload via main api (used by ImportLMS and LecturePlayer)

const uploadLstFile = async (formData) => {
  try {
    const response = await api.post('/api/lst/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const listLstUploads = async () => {
  try {
    const response = await api.get('/api/lst/uploads');
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export default {
  searchInstitutes,
  getCourses,
  getCourseById,
  createCourse,
  updateCourse,
  deleteCourse,
  createCourseFromFile,
  getAQMSCourses,
  importCourseFromAQMS,
  createLesson,
  uploadDocument,
  generateUploadUrl,
  uploadToAzureBlob,
  confirmFileUpload,
  getLessonById,
  getLessonsByCourse,
  generateQuestions,
  generateAdvancedQuestions,
  startQuestionGeneration,
  checkQuestionGenerationStatus,
  saveQuestionSet,
  summarizeLesson,
  editTranscription,
  editSummarization,
  checkSummarizeStatus,
  startTranscriptionJob,
  checkTranscriptionJobStatus,
  startSpeechTranscriptionJob,
  checkSpeechTranscriptionJobStatus,
  getQuestionSetsByLesson,
  getQuestionSetById,
  submitQuestionReview,
  exportQuestionSetToAQMS,
  getLLMUsage,
  getLLMUsageSummary,
  getQuestionById,
  updateQuestion,
  enhanceQuestion,
  deleteQuestion,
  getDashboardStats,
  getUsersWithCounts,
  runAudit,
  downloadAuditCsv,
  downloadAuditPdf,
  getOutcomesByCourse,
  createOutcome,
  uploadBlueprint,
  downloadBlueprint,
  uploadQuestionSetCsv,
  uploadQuestionPaper,
  getQuestionSetSourceFileUrl,
  downloadQuestionSetExcel,
  downloadQuestionSetTemplate,
  getQuestionSetsByLessonQuery,
  getQuestionSetByQuestionSetId,
  createQuestionSet,
  generateQuestionSet,
  createQuestion,
  createQuestionsBulk,
  getLessonByIdWithSas,
  getImsccRawFileBlob,
  getImsccRawFileAsPdfBlob,
  createCourseWithTimestampFallback,
  uploadLstFile,
  listLstUploads
};